import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { LEVELS } from '../../constants/levels';
import Critter from '../glyphs/Critter';
import Glyph from '../glyphs/Glyph';

const W = 390;
const TOP = 170;
const STEP = 168;
const N = LEVELS.length;
const H = TOP + N * STEP + 40;

// Level 1 waits at the bottom; the trail climbs, crossing one stream per level.
const yOf = (level: number) => TOP + (N - level) * STEP + STEP / 2;
const xOf = (level: number) => (level % 2 ? 268 : 122);

const streamPath = (y: number, seed: number) => {
  const top: string[] = [];
  const bottom: string[] = [];
  for (let x = -10; x <= W + 10; x += 20) {
    top.push(`${x},${(y - 13 + 4 * Math.sin(x / 38 + seed)).toFixed(1)}`);
    bottom.unshift(`${x},${(y + 13 + 4 * Math.sin(x / 45 + seed + 1.3)).toFixed(1)}`);
  }
  return `M${top.join(' L')} L${bottom.join(' L')}Z`;
};

const trailPath = (from: number) => {
  const a = { x: xOf(from), y: yOf(from) - 10 };
  const b = { x: xOf(from + 1), y: yOf(from + 1) + 10 };
  const mid = (a.y + b.y) / 2;
  return `M${a.x} ${a.y} C${a.x} ${mid} ${b.x} ${mid} ${b.x} ${b.y}`;
};

function Bridge({ x, y, state }: { x: number; y: number; state: 'crossed' | 'here' | 'ahead' }) {
  const lit = state !== 'ahead';
  const deck = (t: number) => y - 2 - 12 * Math.sin(Math.PI * t);
  return (
    <g>
      {state === 'here' && (
        <motion.ellipse cx={x} cy={y - 4} rx="50" ry="30" fill="hsl(var(--bridge) / 0.14)" stroke="hsl(var(--bridge) / 0.5)" strokeWidth="1.5"
          animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }} />
      )}
      <path d={`M${x - 40} ${y + 4} Q${x} ${y - 30} ${x + 40} ${y + 4}`} fill="none"
        stroke={lit ? 'hsl(var(--bridge-foreground) / 0.55)' : 'hsl(var(--muted-foreground) / 0.35)'}
        strokeWidth="2" strokeDasharray={lit ? undefined : '3 4'} />
      {[0.1, 0.3, 0.5, 0.7, 0.9].map((t) => {
        const px = x - 34 + 68 * t;
        return (
          <rect key={t} x={px - 6} y={deck(t) - 3} width={12} height={7} rx={2}
            transform={`rotate(${(Math.cos(Math.PI * t) * -28).toFixed(1)} ${px} ${deck(t)})`}
            fill={lit ? 'hsl(var(--bridge))' : 'none'}
            stroke={lit ? 'hsl(var(--bridge-foreground) / 0.45)' : 'hsl(var(--muted-foreground) / 0.4)'}
            strokeDasharray={lit ? undefined : '2 2'} />
        );
      })}
      {[-38, 38].map((dx) => (
        <line key={dx} x1={x + dx} x2={x + dx} y1={y - 12} y2={y + 6}
          stroke={lit ? 'hsl(var(--bridge-foreground) / 0.55)' : 'hsl(var(--muted-foreground) / 0.35)'} strokeWidth="2.5" strokeLinecap="round" />
      ))}
    </g>
  );
}

function Tree({ x, y, s = 1 }: { x: number; y: number; s?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`} aria-hidden="true">
      <rect x="-1.5" y="0" width="3" height="10" rx="1" fill="hsl(var(--bridge-foreground) / 0.35)" />
      <circle cx="0" cy="-4" r="9" fill="hsl(var(--primary) / 0.32)" />
      <circle cx="-5" cy="1" r="6" fill="hsl(var(--primary) / 0.26)" />
    </g>
  );
}

interface Props {
  xp: number;
  activeLevel: number;
  avatarId: string;
}

/** The levels as a trail up a valley: every level is one more stream crossed on a bridge. */
export default function BridgeTrail({ xp, activeLevel, avatarId }: Props) {
  const hereRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    hereRef.current?.scrollIntoView({ block: 'center' });
  }, []);

  const next = LEVELS.find((l) => l.level === activeLevel + 1);
  const cur = LEVELS.find((l) => l.level === activeLevel)!;
  const frac = next ? Math.min(1, (xp - cur.minDiamonds) / (next.minDiamonds - cur.minDiamonds)) : 0;
  const pct = (v: number, of: number) => `${(v / of) * 100}%`;

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="block w-full h-auto" aria-hidden="true">
        <defs>
          <linearGradient id="trail-land" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="hsl(var(--bridge-soft))" />
            <stop offset="0.12" stopColor="hsl(var(--background))" />
            <stop offset="1" stopColor="hsl(var(--primary) / 0.10)" />
          </linearGradient>
        </defs>
        <rect width={W} height={H} fill="url(#trail-land)" />

        {/* the summit: where the whole journey leads */}
        <circle cx={W / 2} cy="70" r="34" fill="hsl(var(--bridge) / 0.35)" />
        <circle cx={W / 2} cy="70" r="20" fill="hsl(var(--bridge))" />
        <path d={`M0 150 L80 104 L130 126 L195 78 L262 124 L310 100 L${W} 150 Z`} fill="hsl(var(--primary) / 0.22)" />
        <path d={`M0 162 Q100 132 195 150 T${W} 146 V172 H0Z`} fill="hsl(var(--primary) / 0.12)" />

        {LEVELS.map((l) => (
          <path key={`s${l.level}`} d={streamPath(yOf(l.level), l.level * 1.7)} fill="hsl(var(--primary) / 0.26)" />
        ))}
        {LEVELS.map((l) => (
          <path key={`w${l.level}`} d={`M${xOf(l.level) > 200 ? 30 : 230} ${yOf(l.level) + 2} q12 -5 24 0 t24 0`}
            fill="none" stroke="hsl(var(--background) / 0.85)" strokeWidth="2" strokeLinecap="round" className="wave-drift" />
        ))}

        {LEVELS.slice(0, -1).map((l) => (
          <Tree key={`t${l.level}`} x={xOf(l.level) > 200 ? 330 : 60} y={yOf(l.level) - STEP / 2 - 4} s={0.9 + (l.level % 3) * 0.15} />
        ))}

        {/* the trail between bridges */}
        {LEVELS.slice(0, -1).map((l) => {
          const walked = l.level < activeLevel;
          const partial = l.level === activeLevel;
          return (
            <g key={`p${l.level}`}>
              <path d={trailPath(l.level)} fill="none" stroke="hsl(var(--muted-foreground) / 0.35)" strokeWidth="3" strokeDasharray="2 7" strokeLinecap="round" />
              {(walked || partial) && (
                <motion.path
                  d={trailPath(l.level)} fill="none" stroke="hsl(var(--bridge))" strokeWidth="4" strokeLinecap="round"
                  initial={{ pathLength: 0 }} animate={{ pathLength: walked ? 1 : frac }}
                  transition={{ duration: partial ? 1.2 : 0.4, delay: partial ? 0.4 : 0 }}
                />
              )}
            </g>
          );
        })}

        {LEVELS.map((l) => (
          <Bridge key={`b${l.level}`} x={xOf(l.level)} y={yOf(l.level)}
            state={l.level < activeLevel ? 'crossed' : l.level === activeLevel ? 'here' : 'ahead'} />
        ))}

        <g transform={`translate(${xOf(activeLevel)} ${yOf(activeLevel) - 14})`}>
          <g className="critter-breathe">
            <Critter id={avatarId} size={40} x={-20} y={-36} blink />
          </g>
        </g>
      </svg>

      {/* signposts */}
      {LEVELS.map((l) => {
        const state = l.level < activeLevel ? 'crossed' : l.level === activeLevel ? 'here' : 'ahead';
        const onRight = xOf(l.level) < 200;
        return (
          <div
            key={l.level}
            ref={state === 'here' ? hereRef : undefined}
            className={`absolute w-[44%] -translate-y-1/2 rounded-2xl border px-3 py-2 shadow-sm ${
              state === 'here'
                ? 'bg-card border-bridge ring-2 ring-bridge/30'
                : state === 'crossed'
                  ? 'bg-card/90 border-border'
                  : 'bg-card border-dashed border-border'
            }`}
            style={{ top: pct(yOf(l.level) - 6, H), [onRight ? 'right' : 'left']: '4%' }}
          >
            <div className="flex items-center justify-between gap-2">
              <span className={`text-[11px] font-bold ${state === 'ahead' ? 'text-muted-foreground' : 'text-bridge-foreground'}`}>
                גשר {l.level} · {l.label}
              </span>
              {state === 'crossed' && <Glyph name="check" size={14} title="עברת" />}
            </div>
            <div className={`mt-1 flex items-center gap-1.5 ${state === 'ahead' ? 'opacity-60' : ''}`}>
              {l.art.critter ? <Critter id={l.art.critter} size={22} /> : <Glyph name={l.art.glyph ?? 'gift'} size={20} />}
              <span className="text-sm font-bold text-foreground leading-tight">{l.reward}</span>
            </div>
            {state === 'here' && (
              <p className="mt-1 text-[11px] font-bold text-bridge-foreground flex items-center gap-1">
                {next ? <>את/ה כאן · עוד {next.minDiamonds - xp} <Glyph name="diamond" size={12} /></> : 'הגעת לפסגה'}
              </p>
            )}
            {state === 'ahead' && (
              <p className="mt-1 text-[11px] text-muted-foreground flex items-center gap-1">
                <Glyph name="diamond" size={12} /> {l.minDiamonds}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
